import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";

const field =
  "bg-[#0A0C10] border-white/15 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#FF3B30] rounded-none h-10";

export const AccountTab = () => {
  const navigate = useNavigate();
  const [emailForm, setEmailForm] = useState({ new_email: "", password: "" });
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState("");

  const handleError = (err) => {
    if (err.response?.status === 401) {
      toast.error("Your session expired — please log in again.");
      localStorage.removeItem("ekal_token");
      navigate("/admin/login");
      return;
    }
    toast.error(formatApiError(err.response?.data?.detail), { duration: 8000 });
  };

  const changeEmail = async (e) => {
    e.preventDefault();
    setBusy("email");
    try {
      const { data } = await api.post("/auth/change-email", emailForm);
      toast.success(`Login email updated to ${data.email}. Use it next time you log in.`);
      setEmailForm({ new_email: "", password: "" });
    } catch (err) {
      handleError(err);
    } finally {
      setBusy("");
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      toast.error("New passwords do not match.");
      return;
    }
    setBusy("password");
    try {
      await api.post("/auth/change-password", {
        current_password: pwForm.current,
        new_password: pwForm.next,
      });
      toast.success("Password updated. It takes effect on your next login.");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      handleError(err);
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="admin-account-tab" className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <form onSubmit={changeEmail} className="border border-white/10 bg-[#12151C] p-6 space-y-4" data-testid="change-email-form">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-amber-400">Login Email</p>
          <h3 className="font-heading font-bold uppercase tracking-tight text-xl mt-1">Change Email</h3>
          <p className="text-xs text-slate-500 mt-1">Your session stays active; the new email applies from your next login.</p>
        </div>
        <Input
          data-testid="change-email-input"
          type="email"
          required
          placeholder="New email address"
          value={emailForm.new_email}
          onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
          className={field}
        />
        <Input
          data-testid="change-email-password"
          type="password"
          required
          placeholder="Confirm with current password"
          value={emailForm.password}
          onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })}
          className={field}
        />
        <button
          data-testid="change-email-btn"
          type="submit"
          disabled={busy === "email"}
          className="w-full bg-[#FF3B30] hover:bg-red-600 disabled:opacity-60 text-white text-xs font-mono uppercase tracking-[0.2em] px-5 py-3.5 transition-colors"
        >
          {busy === "email" ? "Updating…" : "Update Email"}
        </button>
      </form>

      <form onSubmit={changePassword} className="border border-white/10 bg-[#12151C] p-6 space-y-4" data-testid="change-password-form">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-amber-400">Password</p>
          <h3 className="font-heading font-bold uppercase tracking-tight text-xl mt-1">Change Password</h3>
          <p className="text-xs text-slate-500 mt-1">Locked out instead? Use “Forgot password?” on the login page to get a reset link by email.</p>
        </div>
        <Input
          data-testid="change-pw-current"
          type="password"
          required
          placeholder="Current password"
          value={pwForm.current}
          onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
          className={field}
        />
        <Input
          data-testid="change-pw-next"
          type="password"
          required
          placeholder="New password (min 8 characters)"
          value={pwForm.next}
          onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })}
          className={field}
        />
        <Input
          data-testid="change-pw-confirm"
          type="password"
          required
          placeholder="Confirm new password"
          value={pwForm.confirm}
          onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
          className={field}
        />
        <button
          data-testid="change-pw-btn"
          type="submit"
          disabled={busy === "password"}
          className="w-full bg-[#FF3B30] hover:bg-red-600 disabled:opacity-60 text-white text-xs font-mono uppercase tracking-[0.2em] px-5 py-3.5 transition-colors"
        >
          {busy === "password" ? "Updating…" : "Update Password"}
        </button>
      </form>
    </div>
  );
};
